import React, { useCallback } from 'react'
import { View, ScrollView } from 'react-native'
import {
  Button,
  Modal,
  Portal,
  Text,
  ActivityIndicator,
  List,
} from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import {
  useMyCollaboratorListsQuery,
  useAssignCollaboratorToListMutation,
} from '../../../../generated/graphql'
import Toast from 'react-native-root-toast'
import * as Sentry from '@sentry/react-native'
import { createStyle } from '../../../../contexts/global-styles.provider'

type AddCollaboratorToListModalProps = {
  visible: boolean
  onDismiss: () => void
  onAssigned: () => void
  collaborator: { collaboratorId: string; username: string } | null
}

export const AddCollaboratorToListModal: React.FC<
  AddCollaboratorToListModalProps
> = ({ visible, onDismiss, onAssigned, collaborator }) => {
  const { t } = useTranslation('nodes')
  const styles = useStyle()

  const { data, loading } = useMyCollaboratorListsQuery({
    skip: !visible,
  })
  const [assignToList, { loading: assigning }] =
    useAssignCollaboratorToListMutation()

  const lists = data?.myCollaboratorLists || []

  const handleSelectList = useCallback(
    async (listId: string) => {
      if (!collaborator?.collaboratorId) return
      try {
        await assignToList({
          variables: {
            input: { listId, collaboratorId: collaborator.collaboratorId },
          },
        })
        Toast.show(t('added-to-list-successfully'))
        onAssigned()
        onDismiss()
      } catch (e) {
        Toast.show((e as Error).message)
        console.error(e)
        Sentry.captureException(e)
      }
    },
    [collaborator, assignToList, onAssigned, onDismiss, t]
  )

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={styles.modalContainer}
      >
        <View style={styles.modalContent}>
          <Text variant="titleLarge" style={styles.modalTitle}>
            {collaborator
              ? t('add-to-list', { username: collaborator.username })
              : t('add-to-list-title')}
          </Text>

          {loading && lists.length === 0 ? (
            <ActivityIndicator style={styles.loader} />
          ) : lists.length === 0 ? (
            <Text style={styles.emptyText}>{t('no-lists-found')}</Text>
          ) : (
            <ScrollView
              style={styles.list}
              contentContainerStyle={styles.listContent}
            >
              {lists.map((list) => (
                <List.Item
                  key={list.id}
                  title={list.name}
                  left={() =>
                    list.color ? (
                      <View
                        style={[
                          styles.colorIndicator,
                          { backgroundColor: list.color },
                        ]}
                      />
                    ) : null
                  }
                  onPress={() => handleSelectList(list.id)}
                  disabled={assigning}
                  style={styles.listItem}
                />
              ))}
            </ScrollView>
          )}

          <Button
            onPress={onDismiss}
            style={styles.closeButton}
            disabled={assigning}
          >
            {t('common:cancel')}
          </Button>
        </View>
      </Modal>
    </Portal>
  )
}

const useStyle = createStyle(({ theme, isMobile }) => ({
  modalContainer: {
    width: isMobile ? '100%' : '60%',
    alignSelf: 'center',
    backgroundColor: theme.colors.surface,
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    borderRadius: 8,
    maxHeight: '80%',
  },
  modalContent: {
    minHeight: 120,
  },
  modalTitle: {
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  list: {
    maxHeight: 300,
    marginBottom: theme.spacing(2),
  },
  listContent: {
    paddingBottom: theme.spacing(1),
  },
  listItem: {
    paddingVertical: theme.spacing(0.5),
  },
  colorIndicator: {
    width: 20,
    height: 20,
    borderRadius: 10,
    marginRight: theme.spacing(1),
    alignSelf: 'center',
  },
  emptyText: {
    textAlign: 'center',
    padding: theme.spacing(4),
    opacity: 0.6,
  },
  loader: {
    marginVertical: theme.spacing(4),
  },
  closeButton: {
    marginTop: theme.spacing(1),
  },
}))
